/**
 * DocumentDetailModal Component
 *
 * Two-pane document viewer:
 *  - Desktop: PDF preview on the left, metadata pane on the right, with
 *    a vertical drag handle between them. Default 60/40, clamped to
 *    30/70 ↔ 80/20.
 *  - Mobile (< md): PDF fills the dialog; the metadata sits in a bottom
 *    sheet draggable up to ~85vh and down to a peeking 20vh handle.
 *
 * Source-aware on the BE: PATCH/DELETE on /v2/provider/documents/{id}
 * route to whichever store owns the row, so the FE doesn't branch on
 * `source` for those calls. The presigned `download_url` returned in
 * the detail response works for both DocRef-backed and legacy
 * DocumentMetadata rows — so we drop the proxy/blob path entirely.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { kindLabel, KIND_LABEL, kindStyle } from '@components/DocumentKind';
import PatientPicker from '@components/Patient/PatientPicker';
import { useDocumentReference } from '@features/patient-artifacts';
import { useSubjectLanguageVariants } from '@features/user';

import type { Patient } from '../../../types/types';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  DragHandle as DragHandleIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { DocumentDetailResponse } from '@store/generated/documents';

import { pickActionableInsuranceCard } from './extractionTypes';
import { InsuranceCardExtractionBanner } from './InsuranceCardExtractionBanner';

import {
  useLazyGetDocumentQuery,
  useSoftDeleteDocumentMutation,
  useUpdateDocumentMetadataMutation,
} from '../store/api';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const formatDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface DocumentDetailModalProps {
  open: boolean;
  documentId: string | null;
  onClose: () => void;
}

const KIND_OPTIONS = Object.entries(KIND_LABEL).map(([value, label]) => ({
  value,
  label: label.replace(/^./, c => c.toUpperCase()),
}));

// Layout constants. Tweaking these is a one-line change.
const DESKTOP_DEFAULT_LEFT_PCT = 60;
const DESKTOP_MIN_LEFT_PCT = 30;
const DESKTOP_MAX_LEFT_PCT = 80;

const MOBILE_DEFAULT_SHEET_VH = 45;
const MOBILE_MIN_SHEET_VH = 18;
const MOBILE_MAX_SHEET_VH = 88;

export const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  open,
  documentId,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const subjectLang = useSubjectLanguageVariants();

  const [fetchDocument, { data: document, isFetching, error }] =
    useLazyGetDocumentQuery();

  // For DocRef-backed rows, also fetch the typed DocRef detail so we
  // can read `metadata.extractions.<kind>` (the unified document
  // detail doesn't carry the dynamic metadata blob). For DocRef-backed
  // rows the document_id IS the DocRef id per the GDN-1032 contract.
  const isDocRefBacked = document?.source === 'document_reference';
  const { documentReference: docRefDetail, refetch: refetchDocRef } =
    useDocumentReference(isDocRefBacked ? document?.document_id ?? null : null);
  const insuranceCardExtraction = pickActionableInsuranceCard(docRefDetail?.metadata);

  const [updateMetadata, { isLoading: isSaving }] = useUpdateDocumentMetadataMutation();
  const [softDelete, { isLoading: isDeleting }] = useSoftDeleteDocumentMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDocKind, setDraftDocKind] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (open && documentId) void fetchDocument({ documentId });
  }, [open, documentId, fetchDocument]);

  useEffect(() => {
    setIsEditing(false);
    setConfirmDeleteOpen(false);
  }, [documentId, open]);

  useEffect(() => {
    if (!document) return;
    setDraftTitle(document.title || document.document_title || '');
    setDraftDocKind(document.doc_kind || document.document_type || '');
  }, [document]);

  const handleDownload = useCallback(() => {
    if (!document?.download_url) return;
    window.open(document.download_url, '_blank', 'noopener,noreferrer');
  }, [document]);

  const handleSaveEdits = useCallback(async () => {
    if (!documentId || !document) return;
    // PATCH route requires at least one field — short-circuit no-op saves.
    const currentTitle = document.title || document.document_title || '';
    const currentKind = document.doc_kind || document.document_type || '';
    const titleChanged = draftTitle !== currentTitle;
    const kindChanged = draftDocKind !== currentKind;
    if (!titleChanged && !kindChanged) {
      setIsEditing(false);
      return;
    }
    try {
      await updateMetadata({
        documentId,
        documentPatchRequest: {
          ...(titleChanged ? { title: draftTitle } : {}),
          ...(kindChanged ? { doc_kind: draftDocKind } : {}),
        },
      }).unwrap();
      setIsEditing(false);
      enqueueSnackbar('Document updated', { variant: 'success' });
      void fetchDocument({ documentId });
    } catch (err) {
      console.error('Update error:', err);
      enqueueSnackbar('Failed to update document', { variant: 'error' });
    }
  }, [
    documentId,
    document,
    draftTitle,
    draftDocKind,
    updateMetadata,
    fetchDocument,
    enqueueSnackbar,
  ]);

  // GDN-1032 follow-up: assign an org-scoped (or otherwise unassigned)
  // document to a patient. Re-uses the unified PATCH endpoint with the
  // new `patient_id` field. The BE rejects null→non-null cross-patient
  // moves with 409, but we only show this picker when patient_id is null
  // to begin with, so that path shouldn't fire.
  const handleAssignPatient = useCallback(
    async (patient: Patient | undefined) => {
      if (!documentId || !patient?.patient_id) return;
      try {
        await updateMetadata({
          documentId,
          documentPatchRequest: { patient_id: patient.patient_id },
        }).unwrap();
        enqueueSnackbar(`Assigned to ${subjectLang.singular}`, { variant: 'success' });
        void fetchDocument({ documentId });
      } catch (err) {
        console.error('Assign error:', err);
        enqueueSnackbar(`Failed to assign ${subjectLang.singular}`, { variant: 'error' });
      }
    },
    [documentId, updateMetadata, fetchDocument, enqueueSnackbar, subjectLang],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!documentId) return;
    try {
      await softDelete({ documentId }).unwrap();
      setConfirmDeleteOpen(false);
      enqueueSnackbar('Document deleted', { variant: 'success' });
      onClose();
    } catch (err) {
      console.error('Delete error:', err);
      enqueueSnackbar('Failed to delete document', { variant: 'error' });
    }
  }, [documentId, softDelete, enqueueSnackbar, onClose]);

  const isPdf = document?.content_type === 'application/pdf';
  const isImage = document?.content_type?.startsWith('image/');
  // Migrated clinical notes are text artifacts (GDN-2433) — previewable, not
  // "Preview not available". text/html is deliberately EXCLUDED: an iframe
  // parses HTML into live (if script-free) markup — rendered forms, external
  // image beacons, spoofable content — so html-typed documents keep the
  // download fallback instead.
  const isText =
    document?.content_type?.startsWith('text/') &&
    !document.content_type.startsWith('text/html');
  const previewUrl = document?.download_url ?? null;

  const displayTitle = useMemo(() => {
    if (!document) return 'Document Details';
    return document.title || document.document_title || document.filename || 'Document';
  }, [document]);

  const kindForChip = document?.doc_kind ?? document?.document_type ?? null;

  // Browser-native PDF viewer via iframe. Avoids react-pdf's shared-worker
  // lifecycle bug (open doc A, close, open doc B → "PDFWorker.create — the
  // worker is being destroyed") and gets us native paging, zoom, search,
  // and print for free. The presigned S3 URL is served with the source's
  // Content-Type, no disposition override, so the browser renders inline.
  const previewNode = previewUrl && isPdf ? (
    <Box
      component="iframe"
      key={previewUrl}
      src={previewUrl}
      title={document!.filename}
      sx={{
        width: '100%',
        height: '100%',
        border: 'none',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    />
  ) : previewUrl && isImage ? (
    <Box
      component="img"
      src={previewUrl}
      alt={displayTitle}
      sx={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        borderRadius: 1,
        boxShadow: 2,
      }}
    />
  ) : previewUrl && isText ? (
    // Plain-text documents (migrated clinical notes, GDN-2433) render
    // natively in an iframe, same mechanism as the PDF branch — the
    // presigned URL serves the source Content-Type, and browsers display
    // non-html text as inert text (never parsed as markup). The empty
    // `sandbox` is defense in depth.
    <Box
      component="iframe"
      key={previewUrl}
      src={previewUrl}
      title={document!.filename}
      sandbox=""
      sx={{
        width: '100%',
        height: '100%',
        border: 'none',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    />
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
      }}
    >
      <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography color="text.secondary" gutterBottom>
        Preview not available for this file type
      </Typography>
      {previewUrl && (
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ mt: 2 }}
        >
          Download File
        </Button>
      )}
    </Box>
  );

  const metadataActions = (
    <>
      <Tooltip title="Download">
        <span>
          <IconButton onClick={handleDownload} size="small" disabled={!previewUrl}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {!isEditing && (
        <>
          <Tooltip title="Edit title and type">
            <IconButton onClick={() => setIsEditing(true)} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => setConfirmDeleteOpen(true)} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </>
  );

  const extractionBanner =
    document && insuranceCardExtraction ? (
      <InsuranceCardExtractionBanner
        documentReferenceId={document.document_id}
        patientId={document.patient_id ?? null}
        extraction={insuranceCardExtraction}
        onSaved={() => void refetchDocRef()}
      />
    ) : null;

  const metadataPaneContent = document ? (
    <MetadataPane
      document={document}
      isEditing={isEditing}
      isSaving={isSaving}
      draftTitle={draftTitle}
      draftDocKind={draftDocKind}
      onDraftTitleChange={setDraftTitle}
      onDraftDocKindChange={setDraftDocKind}
      onCancelEdit={() => setIsEditing(false)}
      onSaveEdits={() => void handleSaveEdits()}
      onAssignPatient={patient => void handleAssignPatient(patient)}
      kindForChip={kindForChip}
      subjectLang={subjectLang}
      extractionBanner={extractionBanner}
    />
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      role="dialog"
      fullScreen={isMobile}
      fullWidth
     maxWidth={"xl"} slotProps={{ paper: {
        sx: {
          borderRadius: isMobile ? 0 : 2,
          height: isMobile ? '100%' : '92vh',
          maxHeight: '92vh',
        },
      } }}>
      <DialogTitle
        sx={{
          m: 0,
          p: 1.5,
          pl: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <DocumentIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" component="span" sx={{ flex: 1, fontWeight: 600 }} noWrap>
          {displayTitle}
        </Typography>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isFetching && !document ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">Failed to load document. Please try again.</Alert>
          </Box>
        ) : document ? (
          isMobile ? (
            <MobileLayout
              previewNode={previewNode}
              metadataPaneContent={metadataPaneContent}
              metadataActions={metadataActions}
            />
          ) : (
            <DesktopLayout
              previewNode={previewNode}
              metadataPaneContent={metadataPaneContent}
              metadataActions={metadataActions}
            />
          )
        ) : null}
      </DialogContent>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        fullWidth
       maxWidth={"xs"}>
        <DialogTitle>Delete this document?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will soft-delete <strong>{displayTitle}</strong>. It will no longer appear in
            lists, but the underlying file is retained for audit.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="error" onClick={() => void handleConfirmDelete()} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// ── Desktop layout: vertical-divider draggable split ──────────────────

function DesktopLayout({
  previewNode,
  metadataPaneContent,
  metadataActions,
}: {
  previewNode: React.ReactNode;
  metadataPaneContent: React.ReactNode;
  metadataActions: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPct, setLeftPct] = useState(DESKTOP_DEFAULT_LEFT_PCT);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      const startX = e.clientX;
      const startPct = leftPct;
      const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 1;

      const onMove = (ev: MouseEvent) => {
        const deltaPct = ((ev.clientX - startX) / containerWidth) * 100;
        const next = Math.min(
          Math.max(startPct + deltaPct, DESKTOP_MIN_LEFT_PCT),
          DESKTOP_MAX_LEFT_PCT,
        );
        setLeftPct(next);
      };
      const onUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [leftPct],
  );

  return (
    <Box
      ref={containerRef}
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}
    >
      <Box
        sx={{
          width: `${leftPct}%`,
          minHeight: 0,
          bgcolor: 'grey.100',
          p: 2,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {previewNode}
      </Box>

      <Box
        onMouseDown={handleMouseDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panes"
        sx={{
          width: 6,
          flexShrink: 0,
          cursor: 'col-resize',
          bgcolor: isDragging ? 'primary.main' : 'divider',
          transition: 'background-color 150ms',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <DragHandleIcon
          sx={{
            fontSize: 14,
            color: 'text.disabled',
            transform: 'rotate(90deg)',
            opacity: 0.6,
          }}
        />
      </Box>

      <Box
        sx={{
          width: `${100 - leftPct}%`,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1 }} />
          {metadataActions}
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>{metadataPaneContent}</Box>
      </Box>
    </Box>
  );
}

// ── Mobile layout: PDF fills, metadata is a draggable bottom sheet ────

function MobileLayout({
  previewNode,
  metadataPaneContent,
  metadataActions,
}: {
  previewNode: React.ReactNode;
  metadataPaneContent: React.ReactNode;
  metadataActions: React.ReactNode;
}) {
  const [sheetVh, setSheetVh] = useState(MOBILE_DEFAULT_SHEET_VH);
  const [isDragging, setIsDragging] = useState(false);

  const startDrag = useCallback(
    (clientY: number) => {
      setIsDragging(true);
      const startY = clientY;
      const startVh = sheetVh;

      const move = (currentY: number) => {
        // Dragging up → bigger sheet; dragging down → smaller. window-based
        // so the same logic works in landscape and portrait.
        const deltaPx = startY - currentY;
        const deltaVh = (deltaPx / window.innerHeight) * 100;
        const next = Math.min(
          Math.max(startVh + deltaVh, MOBILE_MIN_SHEET_VH),
          MOBILE_MAX_SHEET_VH,
        );
        setSheetVh(next);
      };

      const onMouseMove = (ev: MouseEvent) => move(ev.clientY);
      const onTouchMove = (ev: TouchEvent) => {
        const t = ev.touches[0];
        if (t) move(t.clientY);
      };
      const stop = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('mouseup', stop);
        document.removeEventListener('touchend', stop);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('touchmove', onTouchMove, { passive: true });
      document.addEventListener('mouseup', stop);
      document.addEventListener('touchend', stop);
    },
    [sheetVh],
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
      {/* Preview fills the available space; padded so the sheet doesn't
          overlap the active page on first paint. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'grey.100',
          p: 1.5,
          pb: `calc(${sheetVh}vh + 12px)`,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: isDragging ? 'none' : 'padding 200ms',
        }}
      >
        {previewNode}
      </Box>

      {/* Bottom sheet */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${sheetVh}vh`,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          boxShadow: 6,
          display: 'flex',
          flexDirection: 'column',
          transition: isDragging ? 'none' : 'height 200ms',
        }}
      >
        {/* Drag handle. `touchAction: none` lives ONLY here so the
            browser doesn't try to scroll the page while the user is
            dragging the sheet up/down. Putting it on the sheet
            container blocks native scrolling inside the metadata pane
            on mobile — the bug PR review caught. */}
        <Box
          onMouseDown={e => {
            e.preventDefault();
            startDrag(e.clientY);
          }}
          onTouchStart={e => {
            const t = e.touches[0];
            if (t) startDrag(t.clientY);
          }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.75,
            cursor: 'ns-resize',
            userSelect: 'none',
            flexShrink: 0,
            touchAction: 'none',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 2,
              bgcolor: isDragging ? 'primary.main' : 'divider',
              transition: 'background-color 150ms',
            }}
          />
        </Box>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{alignItems: "center",
            px: 2,
            pb: 1,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1 }} />
          {metadataActions}
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>{metadataPaneContent}</Box>
      </Box>
    </Box>
  );
}

// ── Metadata pane (shared by desktop + mobile layouts) ────────────────

function MetadataPane({
  document,
  isEditing,
  isSaving,
  draftTitle,
  draftDocKind,
  onDraftTitleChange,
  onDraftDocKindChange,
  onCancelEdit,
  onSaveEdits,
  onAssignPatient,
  kindForChip,
  subjectLang,
  extractionBanner,
}: {
  document: DocumentDetailResponse;
  isEditing: boolean;
  isSaving: boolean;
  draftTitle: string;
  draftDocKind: string;
  onDraftTitleChange: (next: string) => void;
  onDraftDocKindChange: (next: string) => void;
  onCancelEdit: () => void;
  onSaveEdits: () => void;
  onAssignPatient: (patient: Patient | undefined) => void;
  kindForChip: string | null;
  subjectLang: ReturnType<typeof useSubjectLanguageVariants>;
  /** Pre-rendered banner for any actionable extraction (insurance card,
   *  etc.). Lives at the top of the pane so it's the first thing the
   *  provider sees when opening a doc with a pending extraction. */
  extractionBanner?: React.ReactNode;
}) {
  const color = kindStyle(kindForChip);
  const dateLabel = formatDate(document.effective_date || document.created_at);
  const signedLabel = formatDate(document.signed_at);

  return (
    <Stack spacing={2}>
      {extractionBanner}
      {isEditing ? (
        <Stack spacing={1.5}>
          <TextField
            label="Title"
            size="small"
            value={draftTitle}
            onChange={e => onDraftTitleChange(e.target.value)}
            fullWidth
            autoFocus
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="doc-kind-label">Type</InputLabel>
            <Select
              labelId="doc-kind-label"
              label="Type"
              value={draftDocKind}
              onChange={e => onDraftDocKindChange(e.target.value)}
            >
              <MenuItem value="">
                <em>Unspecified</em>
              </MenuItem>
              {KIND_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button onClick={onCancelEdit} disabled={isSaving} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSaveEdits}
              disabled={isSaving}
              sx={{ textTransform: 'none' }}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
            {document.title || document.document_title || document.filename || 'Document'}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            {kindForChip && (
              <Chip
                label={kindLabel(kindForChip)}
                size="small"
                sx={{
                  bgcolor: color.bg,
                  color: color.fg,
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              />
            )}
            {dateLabel && (
              <Typography variant="body2" color="text.secondary">
                {dateLabel}
              </Typography>
            )}
            {signedLabel && signedLabel !== dateLabel && (
              <Typography variant="body2" color="text.secondary">
                · signed {signedLabel}
              </Typography>
            )}
          </Stack>
          {(document.author_provider_name || document.author_facility) && (
            <Typography variant="body2" color="text.secondary">
              {[document.author_provider_name, document.author_facility]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            {formatFileSize(document.size_bytes)} · {document.filename}
          </Typography>
        </Stack>
      )}

      <Divider />
      <Box>
        <SectionLabel>{subjectLang.singularCap}</SectionLabel>
        {document.patient_id ? (
          <Typography variant="body2">
            {document.patient?.name ||
              [document.patient?.first_name, document.patient?.last_name]
                .filter(Boolean)
                .join(' ') ||
              document.patient_id}
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Not assigned to a {subjectLang.singular}.
            </Typography>
            <PatientPicker onPatientSelect={onAssignPatient} size="small" />
          </Stack>
        )}
      </Box>

      {document.description && (
        <>
          <Divider />
          <Box>
            <SectionLabel>Summary</SectionLabel>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {document.description}
            </Typography>
          </Box>
        </>
      )}

      {document.tags && document.tags.length > 0 && (
        <>
          <Divider />
          <Box>
            <SectionLabel>Tags</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {document.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11, height: 22 }}
                />
              ))}
            </Box>
          </Box>
        </>
      )}

      {document.sections && document.sections.length > 0 && (
        <>
          <Divider />
          <Box>
            <SectionLabel>Sections</SectionLabel>
            <Stack spacing={0.5}>
              {document.sections.map((section, idx) => (
                <SectionRow key={(section.section_id as string) ?? idx} section={section} />
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ fontSize: 10, letterSpacing: 1, display: 'block', mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

function SectionRow({ section }: { section: { [key: string]: unknown } }) {
  const title = (section.title as string | undefined) ?? 'Untitled section';
  const start = section.page_start as number | undefined;
  const end = section.page_end as number | undefined;
  const range =
    start != null && end != null
      ? start === end
        ? `p. ${start}`
        : `pp. ${start}–${end}`
      : null;
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{justifyContent: "space-between", alignItems: "baseline",  py: 0.25 }}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        {title}
      </Typography>
      {range && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
        >
          {range}
        </Typography>
      )}
    </Stack>
  );
}

export default DocumentDetailModal;
